import React, { useEffect, useState, useCallback } from 'react';
import { Bell, Warning, CheckCircle, Sparkle, ArrowsClockwise } from '@phosphor-icons/react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const KIND_LABEL = {
  client_at_risk: { label: 'Cliente en riesgo', color: 'text-amber-300', bg: 'bg-amber-500/15', border: 'border-amber-500/40' },
  quote_at_risk: { label: 'Cotización en riesgo', color: 'text-rose-300', bg: 'bg-rose-500/15', border: 'border-rose-500/40' },
  cold_conversation: { label: 'Conversación fría', color: 'text-sky-300', bg: 'bg-sky-500/15', border: 'border-sky-500/40' },
  low_stock: { label: 'Stock crítico', color: 'text-orange-300', bg: 'bg-orange-500/15', border: 'border-orange-500/40' },
  vendor_slump: { label: 'Bajón de vendedor', color: 'text-purple-300', bg: 'bg-purple-500/15', border: 'border-purple-500/40' },
  hot_lead: { label: 'Oportunidad caliente', color: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40' },
};

const SEV = {
  critical: { dot: '#DC2626', label: 'CRÍTICA', txt: 'text-red-300' },
  warn: { dot: '#D97706', label: 'ATENCIÓN', txt: 'text-amber-300' },
  info: { dot: '#2563EB', label: 'INFO', txt: 'text-blue-300' },
};

const Alertas = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminLike = ['admin', 'supervisor', 'gerente'].includes((user?.role || '').toLowerCase());
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState('all'); // all | critical | warn

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/predictive-alerts/me?limit=200');
      setAlerts(res.data?.alerts || []);
    } catch (e) {
      toast.error('Error al cargar alertas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    try {
      setRunning(true);
      const res = await api.post('/predictive-alerts/admin/run-now');
      const r = res.data || {};
      toast.success(`Motor IA ejecutado · ${r.new_alerts || 0} alertas nuevas`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo ejecutar el motor');
    } finally {
      setRunning(false);
    }
  };

  const dismiss = async (id) => {
    try {
      await api.post(`/predictive-alerts/${id}/dismiss`);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      toast.success('Alerta descartada');
    } catch (e) {
      toast.error('No se pudo descartar');
    }
  };

  const filtered = alerts.filter((a) => filter === 'all' || a.severity === filter);
  const byKind = filtered.reduce((acc, a) => {
    acc[a.kind] = acc[a.kind] || [];
    acc[a.kind].push(a);
    return acc;
  }, {});

  const counts = {
    all: alerts.length,
    critical: alerts.filter((a) => a.severity === 'critical').length,
    warn: alerts.filter((a) => a.severity === 'warn').length,
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="page-alertas">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shrink-0">
            <Sparkle size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-[var(--text)] heading-font">
              Alertas Predictivas IA
            </h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              {isAdminLike
                ? 'Detecciones automáticas en toda la operación. Se actualiza cada 2 horas.'
                : 'Tus alertas personales. Se actualiza cada 2 horas.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="px-3 py-2 border border-[var(--border)] rounded-[10px] text-sm font-bold hover:bg-[var(--surface-muted)] flex items-center gap-1.5"
            data-testid="btn-refresh-alertas"
          >
            <ArrowsClockwise size={14} weight="bold" /> Actualizar
          </button>
          {isAdminLike && (
            <button
              onClick={runNow}
              disabled={running}
              className="px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-[10px] text-sm font-bold disabled:opacity-50 flex items-center gap-1.5"
              data-testid="btn-run-alerts-now"
            >
              <Sparkle size={14} weight="fill" className={running ? 'animate-pulse' : ''} />
              {running ? 'Analizando…' : 'Ejecutar motor IA'}
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { id: 'all', label: `Todas (${counts.all})`, color: 'border-[var(--border)]' },
          { id: 'critical', label: `Críticas (${counts.critical})`, color: 'border-red-500/40 text-red-300' },
          { id: 'warn', label: `Atención (${counts.warn})`, color: 'border-amber-500/40 text-amber-300' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full border text-xs font-bold ${f.color} ${
              filter === f.id ? 'bg-[var(--surface-muted)]' : ''
            }`}
            data-testid={`filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-[var(--text-muted)]">Cargando…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-10 text-center" data-testid="empty-alerts-state">
          <CheckCircle size={48} weight="duotone" className="mx-auto text-emerald-400 mb-3" />
          <p className="text-base font-bold text-[var(--text)]">Sin alertas activas</p>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            La IA monitorea cada 2 horas. Si aparece algo importante, lo verás aquí.
          </p>
        </div>
      ) : (
        Object.entries(byKind).map(([kind, list]) => {
          const meta = KIND_LABEL[kind] || { label: kind, color: 'text-slate-300', bg: 'bg-slate-500/15', border: 'border-slate-500/40' };
          return (
            <div key={kind} className="mb-6" data-testid={`section-${kind}`}>
              <h2 className={`text-xs uppercase tracking-[0.25em] font-black mb-2 ${meta.color}`}>
                {meta.label} · {list.length}
              </h2>
              <div className="space-y-2">
                {list.map((a) => {
                  const sev = SEV[a.severity] || SEV.info;
                  return (
                    <div
                      key={a.id}
                      className={`bg-[var(--surface)] border ${meta.border} rounded-[12px] p-4 hover:bg-[var(--surface-muted)] transition-colors`}
                      data-testid={`alert-card-${a.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className="mt-1 w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ background: sev.dot }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`text-[10px] font-black uppercase tracking-wide ${sev.txt}`}>
                              {sev.label}
                            </span>
                          </div>
                          <p className="text-sm font-bold text-[var(--text)]">{a.title}</p>
                          <p className="text-xs text-[var(--text-muted)] mt-0.5">{a.message}</p>
                          <p className="text-[10px] text-[var(--text-faint)] mt-2">
                            {new Date(a.created_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0">
                          {a.deep_link && (
                            <button
                              onClick={() => navigate(a.deep_link)}
                              className="text-[11px] px-2.5 py-1 bg-[var(--primary)]/15 text-[var(--primary)] rounded-md font-bold hover:bg-[var(--primary)]/25"
                              data-testid={`alert-go-${a.id}`}
                            >
                              Atender →
                            </button>
                          )}
                          <button
                            onClick={() => dismiss(a.id)}
                            className="text-[11px] px-2.5 py-1 border border-[var(--border)] rounded-md text-[var(--text-muted)] hover:bg-[var(--surface-muted)]"
                            data-testid={`alert-dismiss-${a.id}`}
                          >
                            Descartar
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default Alertas;
